import { getAccountContext, setAccountContext } from '@/store/api'
import { branchContextService } from '@/services/branchContextService'
import { populateDataHubBranchSelector } from './branch-selector'

interface EnsureBranchNameOptions {
  branchId: string
  sheetBranchName?: string | null
  selectedBranchName?: string | null
}

export interface EnsureBranchNameResult {
  branchName: string
  renamed: boolean
}

const normalize = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * Ensures branch names pulled from uploads respect branch defaults and UI state.
 * Auto-renames the branch when the Firestore doc still uses the default placeholder.
 */
export async function ensureBranchNameForUpload(options: EnsureBranchNameOptions): Promise<EnsureBranchNameResult> {
  const sheetName = (options.sheetBranchName ?? '').trim()
  const selectedName = (options.selectedBranchName ?? '').trim()
  const resolvedBranchName = sheetName || selectedName || 'Unknown Branch'

  const normalizedResolved = normalize(resolvedBranchName || '')
  const context = getAccountContext()
  const branchMeta = context.userBranches.find((branch) => branch.branchId === options.branchId)
  const canonicalName = branchMeta?.branchName || selectedName || ''
  const normalizedCanonical = normalize(canonicalName || '')
  const wasDefault = branchMeta ? (branchMeta.isDefaultName === false ? false : true) : true
  const shouldAttemptRename = wasDefault && normalizedResolved && normalizedResolved !== normalize('unknown branch')

  let renamed = false

  if (shouldAttemptRename) {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functions = getFunctions()
      const updateBranch = httpsCallable(functions, 'updateBranchNameIfDefault')
      await updateBranch({ branchId: options.branchId, newName: resolvedBranchName })
      renamed = true
    } catch (error: any) {
      if (!error || error.code !== 'failed-precondition') {
        throw error
      }
      if (normalizedCanonical && normalizedResolved !== normalizedCanonical) {
        throw new Error(`Branch name in file ("${resolvedBranchName}") doesn’t match selected branch ("${canonicalName || selectedName || 'Unknown'}"). Please add a new branch in "My Branches" and retry.`)
      }
    }
  } else if (normalizedCanonical && normalizedResolved !== normalizedCanonical) {
    throw new Error(`Branch name in file ("${resolvedBranchName}") doesn’t match selected branch ("${canonicalName || selectedName || 'Unknown'}"). Please add a new branch in "My Branches" and retry.`)
  }

  if (renamed) {
    const updatedBranches = context.userBranches.map((branch) =>
      branch.branchId === options.branchId
        ? { ...branch, branchName: resolvedBranchName, isDefaultName: false }
        : branch,
    )

    setAccountContext({
      accountId: context.accountId,
      planType: context.planType,
      role: context.role,
      userBranches: updatedBranches,
      selectedBranchId: context.selectedBranchId,
      tierLimits: context.tierLimits,
      members: context.members,
    })

    branchContextService.updateBranchName(options.branchId, resolvedBranchName)
    populateDataHubBranchSelector()
    window.dispatchEvent(new CustomEvent('finalytics:branchRenamed', {
      detail: { branchId: options.branchId, branchName: resolvedBranchName },
    }))
  }

  return { branchName: resolvedBranchName, renamed }
}
